import { computed } from 'vue';
import { useHandleCode } from '~/composables/useHandleCode';
import { useFetchWithJwt } from "~/composables/useFetchWithJwt";
import { usePagination } from "~/composables/usePagination";
import { useTerminal } from "~/composables/useTerminal";
import { usePersistentState } from "~/composables/usePersistentState";
import {ApiResponse, ApiResponseWithPage, PageData} from "~/types/response";
import { FacilityData } from "~/types/facility";
import {useState} from "nuxt/app";

export function useFacility() {
	const { handleCode, handleErrorCode } = useHandleCode();
	const { terminals } = useTerminal();
	const { currentPage, pageSize, setParams, setFetchFunction, updatePagination } = usePagination('useFacility');

	const { fetchWithJwt } = useFetchWithJwt();

	// 언어팩에서 불러올 코드 그룹(메인)
	const mainCommonCodeGroup = 'common';

	// 언어팩에서 불러올 코드 그룹(서브)
	const facilityTableHeaderCodes = 'facilityTableHeader';

	// 전체 현장(사이트)정보
	const allFacilityList = useState<FacilityData[]>('allFacilityList', () => []);

	// 현재 선택된 현장(사이트)정보
	const facility = usePersistentState<FacilityData | null>('facility', () => null);

	// 현장(사이트)정보 테이블 헤더
	const facilityTableHeader = [
		{ key: 'facilityName', title: handleCode(mainCommonCodeGroup, facilityTableHeaderCodes, 'facilityName'), sortable: true },
		{ key: 'contact', title: handleCode(mainCommonCodeGroup, facilityTableHeaderCodes, 'contact'), sortable: true },
		{ key: 'facilityAddress', title: handleCode(mainCommonCodeGroup, facilityTableHeaderCodes, 'facilityAddress'), sortable: true },
	]

	// 현장(사이트)정보 테이블 데이터
	const facilityTableData = useState<PageData<FacilityData>>("facilityTableData", () => ({
		content: [],
		totalPages: 0,
		totalElements: 0,
		number: 0,
		size: 0,
		first: false,
		last: false,
	}));

	/**
	 * 현장(사이트)정보를 sessionStorage에 저장
	 * @param facilityData	현장(사이트)정보
	 */
	function setFacility(facilityData: FacilityData) {
		facility.value = facilityData
		terminals.value = facilityData.terminals ?? []
		sessionStorage.setItem('facility', JSON.stringify(facilityData))
		sessionStorage.setItem('terminals', JSON.stringify(terminals.value))
	}

	// 현장 ID
	const facilityId = computed(() => facility.value?.id ?? null);
	const facilityName = computed(() => facility.value?.facilityName ?? '');
	const facilityAlias = computed(() => facility.value?.facilityAlias ?? '');
	const facilityAddress = computed(() => facility.value?.facilityAddress ?? '');
	const facilityContact = computed(() => facility.value?.contact ?? '');

	/**
	 * 현장(사이트)정보 조회(검색용 - ALL)
	 */
	const fetchFacilityAllData = async (): Promise<ApiResponseWithPage<FacilityData>> => {
		return (
			await fetchWithJwt("/facility/search", {
				method: "POST",
				body: JSON.stringify({})
			})
		) as ApiResponseWithPage<FacilityData>;
	}


	/**
	 * 현장(사이트)정보 조회(테이블용)
	 * @param param	현장명, 주소, 구분
	 */
	const fetchFacilityDatas = async (param?: any): Promise<ApiResponseWithPage<FacilityData>> => {
		// param이 있을 경우, 값이 null, undefined, 혹은 빈 문자열이 아닌 항목만 필터링합니다.
		const bodyData = {
			...(param || {}),
			page: currentPage.value - 1 || 0, // 👈 현재 페이지 적용
			size: pageSize.value,
		};

		return (
			await fetchWithJwt("/facility/search", {
				method: "POST",
				body: JSON.stringify(bodyData),
			})
		) as ApiResponseWithPage<FacilityData>;
	}

	/**
	 * 선택한 현장(사이트) 인덱스 조회
	 * @param facilityId	현장(사이트) ID
	 */
	const fetchFacilityPageIndex = async (facilityId: number): Promise<ApiResponse<number>> => {
		return (
			await fetchWithJwt(`/facility/index/${facilityId}`,
				{ method: "GET"}
			)
		) as ApiResponse<number>;
	}


	/**
	 * 현장(사이트)정보 조회(검색용 - ALL)
	 */
	const handleFacilityAllData = async (): Promise<void> => {
		console.log('[useFacility.ts] handleFacilityAllData 함수 시작');
		try {
			const response = await fetchFacilityAllData();
			if (response && response.result === "success") {
				allFacilityList.value = response.data.content;
			} else {
				handleErrorCode(response.message);
			}
		} catch (error) {
			console.error("Error fetching all facility data:", error);
			handleErrorCode('E50001');
		} finally {
			console.log('[useFacility.ts] handleFacilityAllData 함수 종료');
		}
	}

	/**
	 * 현장(사이트)정보 조회(API 호출)
	 * @param param	현장명, 주소, 구분
	 */
	const handleFacilityTableData = async (param?: any): Promise<PageData<FacilityData>> => {
		console.log('[useFacility.ts] handleFacilityTableData 함수 시작');
		try {
			const response = await fetchFacilityDatas(param);
			if (response && response.result === "success") {
				facilityTableData.value = response.data;

				// 페이지네이션 처리
				await updatePagination(response.data as PageData<FacilityData>);
				setParams(param);
				setFetchFunction(handleFacilityTableData);

				// content 배열에서 id 값이 낮은 항목을 찾아 sessionStorage에 저장
				// 로그인시 최초 보여줄 현장 정보로
				// id 값이 높을 수록 펌프가 설치 되어 있지 않을 확률이 높기에 낮은 값으로 저장
				const facilities = response.data.content;

				if (facilities && facilities.length > 0) {
					const firstFacility = facilities.reduce((prev, current) =>
						prev.id < current.id ? prev : current
					);

					// sessionStorage에 facility가 없을 경우에만 저장
					if (!sessionStorage.getItem('facility')) {
						sessionStorage.setItem('facility', JSON.stringify(firstFacility));
						facility.value = firstFacility;
					}

					// sessionStorage에 terminals가 없을 경우에만 저장
					if (!sessionStorage.getItem('terminals')) {
						const terminalList = firstFacility.terminals ?? [];
						sessionStorage.setItem('terminals', JSON.stringify(terminalList));
						terminals.value = terminalList;
					}
				} else {
					// 데이터 없음: 상태 초기화
					facility.value = null;
					sessionStorage.removeItem('facility');

					terminals.value = [];
					sessionStorage.removeItem('terminals');
				}

				return response.data;
			} else {
				handleErrorCode(response.message);
				return {
					content: [],
					totalPages: 0,
					totalElements: 0,
					number: 0,
					size: 0,
					first: false,
					last: false
				};
			}
		} catch (error) {
			console.error("Error fetching facility data:", error);
			handleErrorCode('E50001');
			return {
				content: [],
				totalPages: 0,
				totalElements: 0,
				number: 0,
				size: 0,
				first: false,
				last: false
			};
		} finally {
			console.log('[useFacility.ts] handleFacilityTableData 함수 종료');
		}
	};

	/**
	 * 현장(사이트)정보 선택된 ID로 조회
	 * @param param	현장(사이트) ID
	 */
	const handleFacilityData = async (param: any): Promise<void> => {
		console.log('[useFacility.ts] handleFacilityData 함수 시작');
		try {
			const response = await fetchFacilityDatas(param);
			if (response && response.result === "success") {
				facility.value = response.data.content[0];
			} else {
				handleErrorCode(response.message);
			}
		} catch (error) {
			console.error("Error fetching facility data:", error);
			handleErrorCode('E50001');
		} finally {
			console.log('[useFacility.ts] handleFacilityData 함수 종료');
		}
	}

	/**
	 * 현장(사이트)정보 선택 인덱스 조회
	 * @param facilityId	현장(사이트) ID
	 */
	const handleFacilityPageIndex = async (facilityId: number): Promise<number> => {
		console.log('[useFacility.ts] handleFacilityPageIndex 함수 시작');
		try {
			const response = await fetchFacilityPageIndex(facilityId);
			if (response && response.result === "success") {
				return response.data;
			} else {
				handleErrorCode(response.message);
				return 0;
			}
		} catch (error) {
			console.error("Error fetching facility page index:", error);
			handleErrorCode('E50001');
			return 0;
		} finally {
			console.log('[useFacility.ts] handleFacilityPageIndex 함수 종료');
		}
	}

	/**
	 * 현장(사이트)정보 선택
	 * @param item	선택한 현장(사이트)정보
	 */
	const handleFacilitySelect = (item: FacilityData) => {
		console.log('[useFacility.ts] handleFacilitySelect 함수 시작');
		facility.value = item; // 이걸로 반응형 상태 갱신
		sessionStorage.setItem('facility', JSON.stringify(item));
		terminals.value = item.terminals ?? [];
		sessionStorage.setItem('terminals', JSON.stringify(terminals.value));
		console.log('[useFacility.ts] handleFacilitySelect 함수 종료');
	};

	return {
		allFacilityList,
		facility,
		facilityId,
		facilityName,
		facilityAlias,
		facilityAddress,
		facilityContact,
		facilityTableHeader,
		facilityTableData,
		setFacility,
		fetchFacilityAllData,
		fetchFacilityTableData: fetchFacilityDatas,
		handleFacilityAllData,
		handleFacilityTableData,
		handleFacilitySelect,
		handleFacilityPageIndex,
		handleFacilityData
	}
}